import {
  RatingSelector,
  type StarTooltipLabels,
} from '@/components/ui/csat-feedback/rating-selector';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Card } from '@heidi/design-system/card';
import { Textarea } from '@heidi/design-system/textarea';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import type { CSATRating } from '@heidi/schema/ml-scribe';
import { X } from 'lucide-react';
import { memo, useMemo } from 'react';

export interface ExperienceFeedbackNudgeLabels {
  questionEase: string;
  questionUsefulness: string;
  titlePhase2: string;
  placeholderImprove: string;
  submit: string;
}

interface ExperienceFeedbackNudgeProps {
  /**
   * Ease rating (1-5) or null if not selected.
   */
  easeRating: CSATRating | null;

  /**
   * Usefulness rating (1-5) or null if not selected.
   */
  usefulnessRating: CSATRating | null;

  /**
   * Current comment/detail text.
   */
  detail: string;

  /**
   * Whether the ease rating has been submitted (triggers Phase 2 UI).
   */
  isRatingSubmitted: boolean;

  /**
   * Callback when user selects ease rating (Phase 1). Advances to Phase 2.
   */
  onEaseRatingSubmit: (rating: CSATRating) => void;

  /**
   * Callback when user selects usefulness rating (Phase 2).
   */
  onUsefulnessRatingSubmit: (rating: CSATRating) => void;

  /**
   * Callback when detail text is changed.
   */
  onDetailChange: (detail: string) => void;

  /**
   * Callback when user submits full feedback.
   */
  onSubmit: () => void;

  /**
   * Callback when user dismisses (closes without action).
   */
  onDismiss: () => void;

  /**
   * Whether submit button should be disabled (both ratings required).
   */
  isSubmitDisabled: boolean;

  /**
   * Product-specific copy. Defaults to Evidence experience-feedback strings.
   */
  labels?: ExperienceFeedbackNudgeLabels;

  /**
   * When true, hide the comment field and Submit until usefulness is rated.
   * Work uses this; Evidence keeps the default (comment shown in Phase 2 immediately).
   */
  deferCommentUntilUsefulness?: boolean;

  /**
   * Additional CSS classes.
   */
  className?: string;
}

/**
 * Experience Feedback Nudge (UMUX-Lite).
 *
 * Two-phase flow:
 * - Phase 1: Ease question only (5-star with tooltips). Selecting a star advances to Phase 2.
 * - Phase 2: Both ease and usefulness 5-star scales with tooltips, single free-text field, Submit.
 * - When `deferCommentUntilUsefulness` is set, comment + Submit wait until usefulness is rated.
 *
 * @see docs/design-docs/experience-feedback/README.md
 */
export const ExperienceFeedbackNudge = memo(
  ({
    easeRating,
    usefulnessRating,
    detail,
    isRatingSubmitted,
    onEaseRatingSubmit,
    onUsefulnessRatingSubmit,
    onDetailChange,
    onSubmit,
    onDismiss,
    isSubmitDisabled,
    labels: labelsProp,
    deferCommentUntilUsefulness = false,
    className,
  }: ExperienceFeedbackNudgeProps) => {
    const { t } = useTranslations();

    const labels = useMemo<ExperienceFeedbackNudgeLabels>(
      () =>
        labelsProp ?? {
          questionEase: t('feedback.experienceFeedback.questionEase'),
          questionUsefulness: t('feedback.experienceFeedback.questionUsefulness'),
          titlePhase2: t('feedback.experienceFeedback.titlePhase2'),
          placeholderImprove: t('feedback.experienceFeedback.placeholderImprove'),
          submit: t('feedback.experienceFeedback.submit'),
        },
      [labelsProp, t]
    );

    const easeTooltipLabels = useMemo<StarTooltipLabels>(
      () => [
        t('feedback.experienceFeedback.tooltipEase1'),
        t('feedback.experienceFeedback.tooltipEase2'),
        t('feedback.experienceFeedback.tooltipEase3'),
        t('feedback.experienceFeedback.tooltipEase4'),
        t('feedback.experienceFeedback.tooltipEase5'),
      ],
      [t]
    );

    const usefulnessTooltipLabels = useMemo<StarTooltipLabels>(
      () => [
        t('feedback.experienceFeedback.tooltipUsefulness1'),
        t('feedback.experienceFeedback.tooltipUsefulness2'),
        t('feedback.experienceFeedback.tooltipUsefulness3'),
        t('feedback.experienceFeedback.tooltipUsefulness4'),
        t('feedback.experienceFeedback.tooltipUsefulness5'),
      ],
      [t]
    );

    const handleEaseClick = (newRating: CSATRating | null) => {
      if (newRating !== null) onEaseRatingSubmit(newRating);
    };

    const handleUsefulnessClick = (newRating: CSATRating | null) => {
      if (newRating !== null) onUsefulnessRatingSubmit(newRating);
    };

    const title = isRatingSubmitted ? labels.titlePhase2 : labels.questionEase;

    const placeholderImprove = labels.placeholderImprove;

    const showCommentAndSubmit =
      !deferCommentUntilUsefulness || usefulnessRating !== null;

    return (
      <Card
        className={cn(
          'fixed bottom-4 right-4 z-[60]',
          'w-full max-w-xs',
          'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4',
          'motion-safe:duration-300',
          'shadow-lg',
          className
        )}
      >
        <div className="relative flex flex-col gap-3 p-4">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            iconOnly
            onClick={onDismiss}
            aria-label={t('common.close')}
            className="absolute end-2 top-2"
          >
            <X />
          </Button>

          <TypographyP2Bold className="pe-6">{title}</TypographyP2Bold>

          {!isRatingSubmitted ? (
            <>
              {/* Phase 1: Ease question only */}
              <RatingSelector
                rateType={easeRating}
                onChangeRating={handleEaseClick}
                variant="star"
                tooltipLabels={easeTooltipLabels}
              />
            </>
          ) : (
            <div className="flex flex-col">
              {/* Phase 2: 20px only between 1st–2nd and 2nd–3rd question */}
              <div className="flex flex-col gap-2">
                <TypographyP2 className="text-text-secondary">
                  {labels.questionEase}
                </TypographyP2>
                <RatingSelector
                  rateType={easeRating}
                  onChangeRating={handleEaseClick}
                  variant="star"
                  tooltipLabels={easeTooltipLabels}
                />
              </div>
              <div className="mt-5 flex flex-col gap-2">
                <TypographyP2 className="text-text-secondary">
                  {labels.questionUsefulness}
                </TypographyP2>
                <RatingSelector
                  rateType={usefulnessRating}
                  onChangeRating={handleUsefulnessClick}
                  variant="star"
                  tooltipLabels={usefulnessTooltipLabels}
                />
              </div>
              {showCommentAndSubmit ? (
                <>
                  <Textarea
                    value={detail}
                    onChange={(e) => onDetailChange(e.target.value)}
                    placeholder={placeholderImprove}
                    className="mt-5 min-h-[80px] resize-none"
                    rows={3}
                  />
                  <div className="mt-4 flex items-center justify-end">
                    <Button size="sm" onClick={onSubmit} disabled={isSubmitDisabled}>
                      {labels.submit}
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>
      </Card>
    );
  }
);

ExperienceFeedbackNudge.displayName = 'ExperienceFeedbackNudge';
